<template>
  <div class="org-charts pa-4">
    <div class="d-flex flex-wrap align-center mb-4">
      <h2 class="org-charts__title mr-4">Organigramas</h2>
      <v-spacer />
      <v-btn outlined :to="{ name: 'ClientOrgChartUnassigned' }">
        <v-icon left>mdi-account-question-outline</v-icon>
        Contactos sin empresa ({{ unassignedCandidates }})
      </v-btn>
    </div>

    <v-card class="mb-6">
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              label="Buscar empresa"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="countryOptionId"
              :items="countries"
              item-text="label"
              item-value="id"
              label="País"
              clearable
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="typeOptionId"
              :items="types"
              item-text="label"
              item-value="id"
              label="Tipo"
              clearable
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-alert v-if="pipedriveError" type="warning" text class="mx-4 mb-4" data-test="pipedrive-alert">
        {{ pipedriveError }}
      </v-alert>

      <v-data-table
        v-else
        :headers="headers"
        :items="organizations"
        item-key="pipedriveOrgId"
        :loading="loadingOrganizations"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        no-data-text="No hay empresas con estos filtros"
        loading-text="Cargando desde Pipedrive…"
        class="org-charts__table"
        @click:row="openOrganization"
      >
        <template #[`item.name`]="{ item }">
          <span class="org-charts__name">{{ item.name }}</span>
          <v-chip
            v-if="item.possibleDuplicates && item.possibleDuplicates.length"
            x-small
            outlined
            class="ml-2"
            @click.stop="openDuplicate(item)"
          >
            <v-icon x-small left>mdi-content-duplicate</v-icon>
            Posible duplicado
          </v-chip>
        </template>
        <template #[`item.peopleCount`]="{ item }">
          <span class="pw-num">{{ item.peopleCount }}</span>
        </template>
        <template #[`item.placedCount`]="{ item }">
          <span class="pw-num">{{ item.placedCount }}</span>
        </template>
        <template #[`item.pendingCandidates`]="{ item }">
          <span class="pw-num">{{ item.pendingCandidates }}</span>
        </template>
      </v-data-table>
    </v-card>

    <v-card>
      <v-card-title class="org-charts__section">
        Sin vincular a Pipedrive ({{ unlinkedTotal }})
      </v-card-title>
      <v-card-subtitle>Empresas de la investigación que todavía no están en Pipedrive.</v-card-subtitle>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="artifactSearch"
              label="Buscar empresa"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="artifactCountry"
              :items="artifactCountries"
              label="País"
              clearable
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-data-table
        :headers="artifactHeaders"
        :items="artifactCompanies"
        :loading="loadingArtifacts"
        :options.sync="artifactOptions"
        :server-items-length="unlinkedTotal"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        no-data-text="No hay empresas sin vincular"
        loading-text="Cargando empresas..."
      >
        <template #[`item.pendingCandidates`]="{ item }">
          <span class="pw-num">{{ item.pendingCandidates }}</span>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn small depressed color="primary" @click="openLink(item)">Vincular</v-btn>
        </template>
      </v-data-table>
    </v-card>

    <duplicate-org-modal v-model="duplicateOpen" :organization="duplicateOrganization" @dismissed="onDuplicateDismissed" />
    <link-company-modal v-model="linkOpen" :company="linkCompany" @linked="onLinked" />

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ClientOrgChartsService from '../services/client-org-charts.service';
import DuplicateOrgModal from '../components/DuplicateOrgModal.vue';
import LinkCompanyModal from '../components/LinkCompanyModal.vue';
import { utilsMixin } from '../mixins/utilsMixin';
import { ARTIFACT_COUNTRIES, errorMessage, isPipedriveError } from '../utils/org-chart.utils';

const SEARCH_DEBOUNCE_MS = 300;

export default {
  name: 'ClientOrgCharts',
  components: { DuplicateOrgModal, LinkCompanyModal },
  mixins: [utilsMixin],
  data() {
    return {
      countryOptionId: null,
      typeOptionId: null,
      search: '',
      options: { page: 1, itemsPerPage: 25 },
      organizations: [],
      total: 0,
      countries: [],
      types: [],
      unassignedCandidates: 0,
      loadingOrganizations: false,
      pipedriveError: '',
      artifactCountry: null,
      artifactSearch: '',
      artifactOptions: { page: 1, itemsPerPage: 10 },
      artifactCompanies: [],
      unlinkedTotal: 0,
      loadingArtifacts: false,
      duplicateOpen: false,
      duplicateOrganization: null,
      linkOpen: false,
      linkCompany: null,
      searchTimer: null,
      artifactSearchTimer: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      headers: [
        { text: 'Empresa', value: 'name', sortable: false },
        { text: 'País', value: 'country', sortable: false },
        { text: 'Tipo', value: 'companyType', sortable: false },
        { text: 'Personas', value: 'peopleCount', sortable: false, align: 'end' },
        { text: 'En el árbol', value: 'placedCount', sortable: false, align: 'end' },
        { text: 'Por revisar', value: 'pendingCandidates', sortable: false, align: 'end' }
      ],
      artifactHeaders: [
        { text: 'Empresa', value: 'name', sortable: false },
        { text: 'País', value: 'country', sortable: false },
        { text: 'Por revisar', value: 'pendingCandidates', sortable: false, align: 'end' },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ],
      artifactCountries: ARTIFACT_COUNTRIES
    };
  },
  watch: {
    countryOptionId() {
      this.resetPageAndLoad();
    },
    typeOptionId() {
      this.resetPageAndLoad();
    },
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.resetPageAndLoad, SEARCH_DEBOUNCE_MS);
    },
    options: {
      deep: true,
      handler(next, prev) {
        if (!prev || next.page !== prev.page || next.itemsPerPage !== prev.itemsPerPage) this.loadOrganizations();
      }
    },
    artifactCountry() {
      this.resetArtifactPageAndLoad();
    },
    artifactSearch() {
      clearTimeout(this.artifactSearchTimer);
      this.artifactSearchTimer = setTimeout(this.resetArtifactPageAndLoad, SEARCH_DEBOUNCE_MS);
    },
    artifactOptions: {
      deep: true,
      handler(next, prev) {
        if (!prev || next.page !== prev.page || next.itemsPerPage !== prev.itemsPerPage) this.loadArtifactCompanies();
      }
    }
  },
  created() {
    this.loadOrganizations();
    this.loadArtifactCompanies();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.artifactSearchTimer);
  },
  methods: {
    resetPageAndLoad() {
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.loadOrganizations();
    },
    resetArtifactPageAndLoad() {
      if (this.artifactOptions.page !== 1) this.artifactOptions = { ...this.artifactOptions, page: 1 };
      else this.loadArtifactCompanies();
    },
    async loadOrganizations() {
      this.loadingOrganizations = true;
      const res = await ClientOrgChartsService.getOrganizations({
        countryOptionId: this.countryOptionId,
        typeOptionId: this.typeOptionId,
        search: this.search ? this.search.trim() : '',
        page: this.options.page,
        pageSize: this.options.itemsPerPage
      });
      this.loadingOrganizations = false;
      if (res.status === 'ok') {
        this.pipedriveError = '';
        this.applyOrganizations(res);
        return;
      }
      if (isPipedriveError(res.code)) {
        this.pipedriveError = res.message || 'No pudimos conectar con Pipedrive. Intenta en unos minutos.';
        return;
      }
      this.showErrorSnackbar(errorMessage(res, 'No pudimos cargar las empresas.'));
    },
    applyOrganizations(res) {
      const meta = res.meta || {};
      this.organizations = res.data || [];
      this.total = meta.total || 0;
      if (meta.countries) this.countries = meta.countries;
      if (meta.types) this.types = meta.types;
      this.unassignedCandidates = meta.unassignedCandidates || 0;
    },
    async loadArtifactCompanies() {
      this.loadingArtifacts = true;
      const res = await ClientOrgChartsService.getArtifactCompanies({
        country: this.artifactCountry,
        search: this.artifactSearch ? this.artifactSearch.trim() : '',
        page: this.artifactOptions.page,
        pageSize: this.artifactOptions.itemsPerPage
      });
      this.loadingArtifacts = false;
      if (res.status === 'ok') {
        this.artifactCompanies = res.data || [];
        this.unlinkedTotal = (res.meta && res.meta.total) || 0;
      } else {
        this.showErrorSnackbar(errorMessage(res, 'No pudimos cargar las empresas sin vincular.'));
      }
    },
    openOrganization(item) {
      this.$router.push({ name: 'ClientOrgChartDetail', params: { pipedriveOrgId: String(item.pipedriveOrgId) } });
    },
    openDuplicate(item) {
      this.duplicateOrganization = item;
      this.duplicateOpen = true;
    },
    onDuplicateDismissed({ pipedriveOrgIdB }) {
      const organization = this.duplicateOrganization;
      organization.possibleDuplicates = organization.possibleDuplicates.filter(dup => dup.pipedriveOrgId !== pipedriveOrgIdB);
      this.showSuccessSnackbar('Listo, no las volveremos a marcar');
    },
    openLink(item) {
      this.linkCompany = item;
      this.linkOpen = true;
    },
    onLinked({ pipedriveOrgId }) {
      this.$router.push({ name: 'ClientOrgChartDetail', params: { pipedriveOrgId: String(pipedriveOrgId) } });
    }
  }
};
</script>

<style scoped>
.org-charts {
  max-width: 100%;
}

.org-charts__title {
  font-size: 22px;
  font-weight: 600;
  color: var(--pw-text);
}

.org-charts__section {
  font-size: 16px;
}

.org-charts__name {
  font-weight: 500;
}

.org-charts__table >>> tbody tr {
  cursor: pointer;
}
</style>
